import { computed, ref, watch } from 'vue'
import { usePreferredDark } from '@vueuse/core'
import { useRoute } from 'vue-router'
import { updateUiPreferences } from '@/api/user.api'
import useUser from '@/composables/useUser'
import {
    applyResolvedTheme,
    isThemeExemptHere,
    isThemePreference,
    isThemeToggleHotkey,
    normalizeThemePreference,
    oppositeResolvedTheme,
    readStoredThemePreference,
    resolveThemePreference,
    writeStoredThemePreference,
    THEME_STORAGE_KEY,
    THEME_SYNC_CHANNEL,
    type ResolvedTheme,
    type ThemePreference,
} from '@/theme/appearance'

const preference = ref<ThemePreference>(readStoredThemePreference())
let applyHooked = false
let syncChannel: BroadcastChannel | null = null
let syncStarted = false
let shortcutHooked = false

function currentPathname(): string {
    if (typeof window !== 'undefined' && window.location?.pathname) {
        return window.location.pathname
    }
    return '/'
}

/** True when this document is the onboarding (or other) preview inside `/dev/testing`. */
function isDevHarnessPreviewFrame(): boolean {
    if (typeof window === 'undefined' || window.self === window.top) return false
    try {
        return window.top?.location.pathname.startsWith('/dev/testing') ?? false
    }
    catch {
        return false
    }
}

function applyFromState(pref = preference.value, systemPrefersDark?: boolean) {
    const systemDark = systemPrefersDark
        ?? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    const resolved = resolveThemePreference(pref, systemDark)
    const dark = !isThemeExemptHere(currentPathname()) && resolved === 'dark'
    applyResolvedTheme(dark ? 'dark' : 'light', undefined, pref)
}

function applyRemotePreference(raw: unknown) {
    const theme = normalizeThemePreference(raw)
    if (theme === preference.value) return
    preference.value = theme
    writeStoredThemePreference(theme)
    applyFromState(theme)
}

function broadcastThemePreference(theme: ThemePreference) {
    try {
        syncChannel?.postMessage(theme)
    }
    catch {
        // Channel closed or unavailable.
    }
}

function startThemeSync() {
    if (syncStarted || typeof window === 'undefined') return
    syncStarted = true

    try {
        syncChannel = new BroadcastChannel(THEME_SYNC_CHANNEL)
        syncChannel.onmessage = (event) => {
            applyRemotePreference(event.data)
        }
    }
    catch {
        syncChannel = null
    }

    window.addEventListener('storage', (event) => {
        if (event.key !== THEME_STORAGE_KEY) return
        applyRemotePreference(event.newValue)
    })
}

export default function useTheme() {
    const preferredDark = usePreferredDark()
    const route = useRoute()
    const { user, setUser } = useUser()

    const resolved = computed<ResolvedTheme>(() =>
        resolveThemePreference(preference.value, preferredDark.value),
    )

    const isDark = computed(() => {
        if (isThemeExemptHere(currentPathname())) return false
        return resolved.value === 'dark'
    })

    if (!applyHooked) {
        applyHooked = true
        startThemeSync()
        // The path only matters for theme-exempt public pages; query and hash changes never do.
        watch(
            [preference, preferredDark, () => route.path],
            () => applyFromState(preference.value, preferredDark.value),
            { immediate: true },
        )
        watch(
            () => (user.value?.id ? user.value.ui_preferences : undefined),
            (prefs) => {
                // The harness preview iframe should follow the sidebar toggle,
                // not a stale copy of the user's saved appearance prefs.
                if (isDevHarnessPreviewFrame()) return
                if (!user.value?.id) return
                const raw = prefs?.appearance?.theme
                if (!isThemePreference(raw)) return
                if (raw !== preference.value) {
                    preference.value = raw
                    writeStoredThemePreference(raw)
                }
            },
        )
    }

    async function setThemePreference(next: ThemePreference) {
        const theme = normalizeThemePreference(next)
        preference.value = theme
        writeStoredThemePreference(theme)
        applyFromState(theme, preferredDark.value)
        broadcastThemePreference(theme)
        if (!user.value?.id) return
        const resp = await updateUiPreferences({ appearance: { theme } })
        if (resp?.ui_preferences?.appearance?.theme && isThemePreference(resp.ui_preferences.appearance.theme)) {
            setUser({ ...user.value, ui_preferences: resp.ui_preferences })
        }
    }

    function toggleLightDark() {
        if (isThemeExemptHere(currentPathname())) return
        void setThemePreference(oppositeResolvedTheme(resolved.value))
    }

    if (!shortcutHooked && typeof window !== 'undefined') {
        shortcutHooked = true
        window.addEventListener('keydown', (event) => {
            if (!isThemeToggleHotkey(event)) return
            if (isThemeExemptHere(currentPathname())) return
            event.preventDefault()
            event.stopPropagation()
            toggleLightDark()
        }, true)
    }

    return {
        preference,
        resolved,
        isDark,
        setThemePreference,
        toggleLightDark,
    }
}
